import { useEffect, useState } from "react";
import { CheckCircle } from "lucide-react";

const benefits = [
  "Independent supervisors who report only to you.",
  "Milestone-based verification before any payment is released.",
  "Risk flag system catches issues weeks ahead.",
];

const stats = [
  { value: "97%", label: "On-time delivery" },
  { value: "23%", label: "Budget protection" },
  { value: "4.2x", label: "Risk caught early" },
  { value: "4.9/5", label: "Client satisfaction" },
];

export function BenefitsSection() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const section = document.getElementById("benefits-section");
    if (section) observer.observe(section);

    return () => observer.disconnect();
  }, []);

  return (
    <section id="benefits-section" className="relative py-24 md:py-32 lg:py-40 bg-muted/30">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="mb-16">
          <h2
            className={`text-5xl md:text-6xl lg:text-7xl font-display tracking-tight leading-[1.1] transition-all duration-1000 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            The kind of trust
            <br />
            <span className="text-muted-foreground">that ships projects.</span>
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          <div className="space-y-6">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className={`flex gap-4 items-start transition-all duration-1000 ${
                  isVisible
                    ? "opacity-100 translate-x-0"
                    : "opacity-0 -translate-x-8"
                }`}
                style={{ transitionDelay: `${100 + index * 100}ms` }}
              >
                <CheckCircle className="w-6 h-6 text-[#eca8d6] flex-shrink-0 mt-1" />
                <p className="text-lg text-muted-foreground leading-relaxed">
                  {benefit}
                </p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-6">
            {stats.map((stat, index) => (
              <div
                key={index}
                className={`p-8 border border-foreground/10 bg-background rounded-lg transition-all duration-1000 ${
                  isVisible
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-8"
                }`}
                style={{ transitionDelay: `${200 + index * 100}ms` }}
              >
                <div className="text-4xl font-display mb-2">{stat.value}</div>
                <div className="text-sm text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div
          className={`mt-16 p-8 border border-foreground/10 bg-background rounded-lg transition-all duration-1000 delay-[500ms] ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <p className="text-xl text-muted-foreground leading-relaxed">
            <span className="font-semibold text-foreground">Trusted by 240+ clients</span> across 10 industries.
          </p>
        </div>
      </div>
    </section>
  );
}
